import { useMemo, useCallback } from 'react';

import { XOR } from '../../components/utils/typescript-util/xor';
import { useWindowDevice } from '../use-window-device/use-window-device';

type RenditionMap = { [x in 'mobile' | 'tablet']: string };

const defaultRendition: RenditionMap = {
    mobile: 'mobile',
    tablet: 'tablet',
};

export const useAemImageRendition = ({
    src,
    renditionsMap,
    removeDomain,
}: {
    src: string | undefined;
    renditionsMap: XOR<
        { [x in 'mobile' | 'tablet']: 'desktop' | 'mobile' | 'tablet' },
        'default'
    >;
    removeDomain?: boolean;
}) => {
    const windowWidth = useWindowDevice();

    const imgPrefix = useMemo(
        () => (removeDomain ? '' : process.env.REACT_APP_AEM_BASE_URL),
        [src]
    );
    const renderedSrc = useMemo(() => (src ? imgPrefix + src : ''), [
        src,
        imgPrefix,
    ]);

    const renditionsToUse = useMemo<RenditionMap>(
        () => (renditionsMap === 'default' ? defaultRendition : renditionsMap),
        [renditionsMap]
    );

    const imageName = useMemo(() => {
        if (src) {
            const indexOfSlash = src.lastIndexOf('/');
            const indexOfSuffix = src.lastIndexOf('.png');
            return src.slice(indexOfSlash + 1, indexOfSuffix);
        }
    }, [src]);

    const getRenditionName = useCallback(() => {
        if (windowWidth && ['mobile', 'tablet'].includes(windowWidth)) {
            const renditionName =
                renditionsToUse[windowWidth as 'mobile' | 'tablet'];
            if (renditionName == '' || renditionName == 'desktop') {
                return null;
            }

            const suffix: string = renditionName.endsWith('.png') ? '' : '.png';
            return imageName + '-' + renditionName + suffix;
        }
    }, [windowWidth, renditionsToUse]);

    const srcString = useMemo<string>(() => {
        if (!windowWidth) return '';
        if (windowWidth == 'desktop') {
            return renderedSrc;
        }
        const renditionPath = getRenditionName();

        if (!renditionPath) {
            return renderedSrc;
        }

        return renderedSrc + `/jcr:content/renditions/` + renditionPath;
    }, [windowWidth, renderedSrc]);

    return srcString;
};
